import { memo } from 'react';
import { Link } from 'react-router-dom';
import { Minus, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from '$lib/components/ui/Badge';
import { cn } from '$lib/lib/cn';
import { formatDate, formatKr, formatKrPerSqm, formatRooms, formatSqm, formatYear } from '$lib/lib/formatters';
import type { ComparableDTO } from '$lib/api/types';

const COMPONENT_LABEL: Record<string, string> = {
  sizeMatch: 'm²',
  roomsMatch: 'rms',
  yearMatch: 'yr',
  distance: 'dist',
  recency: 'rcn',
  sourceQuality: 'src',
  energyMatch: 'en'
};

function ScorePill({ value }: { value: number }) {
  const pct = Math.round(value * 100);
  const tone = value >= 0.8 ? 'positive' : value >= 0.6 ? 'accent' : value >= 0.4 ? 'warning' : 'danger';
  return (
    <Badge tone={tone} className="num h-5 min-w-[2.6rem] justify-center font-semibold">
      {pct}
    </Badge>
  );
}

function ScoreBreakdown({ score }: { score: ComparableDTO['score'] }) {
  const entries = Object.entries(score.components) as Array<[string, number]>;
  return (
    <ul className="grid grid-cols-3 gap-x-2 gap-y-0.5 text-2xs text-fg-muted" data-testid="score-breakdown">
      {entries.map(([key, val]) => (
        <li key={key} className="flex items-center justify-between gap-1">
          <span>{COMPONENT_LABEL[key] ?? key}</span>
          <span className="num text-fg">{Math.round(val * 100)}</span>
        </li>
      ))}
    </ul>
  );
}

function distanceLabel(metres: number): string {
  if (metres < 1000) return `${metres} m`;
  return `${(metres / 1000).toFixed(metres < 5000 ? 1 : 0)} km`;
}

export interface ComparableRowProps {
  comparable: ComparableDTO;
  /** Whether the row is currently excluded from the analyst's view. */
  excluded?: boolean;
  /** When true, the row is fading because it was just clicked. */
  pendingExclude?: boolean;
  onExclude: (comparable: ComparableDTO) => void;
  /** Used to keep keyboard navigation predictable when there are many rows. */
  expandedByDefault?: boolean;
}

function ComparableRowImpl({ comparable, excluded, pendingExclude, onExclude, expandedByDefault }: ComparableRowProps) {
  const { t } = useTranslation();
  return (
    <li
      data-testid="comparable-row"
      data-comparable-id={comparable.id}
      className={cn(
        'row-line group flex flex-col gap-1.5 px-3 py-2 text-xs transition-colors',
        excluded ? 'bg-surface-2/40 opacity-65' : 'hover:bg-surface-2',
        pendingExclude && 'opacity-40'
      )}
      aria-label={t('comparables.rankAndAddress', { rank: comparable.rank, address: comparable.address })}
    >
      <div className="flex items-start gap-2">
        <span
          aria-label={t('comparables.rank')}
          className={cn(
            'num mt-0.5 inline-flex h-5 min-w-[2rem] items-center justify-center rounded-xs border px-1 text-2xs font-semibold',
            excluded
              ? 'border-rule bg-surface-2 text-fg-subtle'
              : 'border-rule-strong bg-surface text-fg'
          )}
        >
          {excluded ? '—' : `#${comparable.rank}`}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <Link
              to={`/units/${comparable.unitId}`}
              className="truncate text-sm font-medium text-fg hover:text-accent"
              title={comparable.address}
            >
              {comparable.address}
            </Link>
            <span className="num text-2xs text-fg-subtle">{comparable.postnummer}</span>
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-2xs text-fg-muted">
            <span className="num text-fg">{formatSqm(comparable.m2)}</span>
            <span>·</span>
            <span className="num text-fg">{formatRooms(comparable.rooms)} {t('comparables.rooms')}</span>
            <span>·</span>
            <span className="num text-fg">{formatYear(comparable.yearBuilt)}</span>
            <span>·</span>
            <span className="num text-fg-muted">{distanceLabel(comparable.distanceM)}</span>
            <span>·</span>
            <span className="text-fg-muted">{comparable.source}</span>
            <span>·</span>
            <span>{t('comparables.listed', { date: formatDate(comparable.listedAt) })}</span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className="num text-sm text-fg">{formatKr(comparable.rentKrPerMonth)}</span>
          <span className="num text-2xs text-fg-muted">{formatKrPerSqm(comparable.krPerSqm)}</span>
        </div>
        <div className="flex flex-col items-end gap-1">
          <ScorePill value={comparable.score.total} />
          {!excluded && (
            <button
              type="button"
              data-testid="exclude-button"
              aria-label={t('comparables.rankExcludeAriaLabel', { address: comparable.address })}
              onClick={() => onExclude(comparable)}
              disabled={pendingExclude}
              className={cn(
                'inline-flex h-6 items-center gap-1 rounded-xs border border-rule px-1.5 text-2xs text-fg-muted',
                'opacity-60 transition-opacity hover:opacity-100 hover:text-danger hover:border-danger/60 focus-visible:opacity-100',
                'disabled:pointer-events-none'
              )}
            >
              <Minus size={10} />
              {t('comparables.exclude')}
            </button>
          )}
        </div>
      </div>
      <details
        open={expandedByDefault}
        className="group/d ml-9 rounded-xs border border-rule bg-surface-2 px-2 py-1.5"
      >
        <summary className="cursor-pointer list-none text-2xs text-fg-subtle hover:text-fg-muted">
          {t('comparables.scoreBreakdown')}
        </summary>
        <div className="mt-1.5">
          <ScoreBreakdown score={comparable.score} />
          {comparable.constraintsPassed.length > 0 && (
            <div className="mt-1.5 flex flex-wrap items-center gap-1">
              {comparable.constraintsPassed.map((c) => (
                <span
                  key={c}
                  className="rounded-xs border border-rule bg-surface px-1 py-px text-2xs text-fg-muted"
                >
                  {c}
                </span>
              ))}
            </div>
          )}
        </div>
      </details>
      {excluded && (
        <p className="ml-9 flex items-center gap-1 text-2xs text-fg-subtle">
          <X size={10} aria-hidden /> {t('comparables.excludedFromView')}
        </p>
      )}
    </li>
  );
}

export const ComparableRow = memo(ComparableRowImpl);
